import { describeStaffFailure, STAFF_WORDS } from "../failure-words";

// How the diagnostics pages tell a staff member what went wrong. Rules:
//   1. No id, key, run reference, reason or provider text goes into a message.
//   2. A refusal for access (403) and a missing record (404) read the same where the record is customer data.
//   3. A conflict says nothing was changed and that the page shows the latest.
//   4. A failure that may have run says so, and whether the SAME action may be sent again (the same Idempotency-Key makes the server run it
//      once). A failure NEVER reads as success, and nothing here marks an incident resolved.
// It reads every client error family (`describeStaffFailure`): the shared transport (payments and diagnostics) and the jobs client.

export type DiagnosticsFailureKind =
  | "conflict"
  | "invalid"
  | "forbidden"
  | "not_found"
  | "unauthenticated"
  | "rate_limited"
  | "unavailable"
  | "network"
  | "response"
  | "unknown";

export type DiagnosticsFailure = Readonly<{
  kind: DiagnosticsFailureKind;
  message: string;
  /** The same action may be sent again with the SAME idempotency key: it may or may not have run. */
  retryable: boolean;
  /** The page must read again: what the person saw is out of date. */
  reload: boolean;
}>;

const NEUTRAL_FORBIDDEN =
  "You cannot do this. If you think you should be able to, ask an administrator or a supervisor.";

export function describeDiagnosticsError(error: unknown): DiagnosticsFailure {
  const { api, kind, message, retryable, reload } = describeStaffFailure(error, {
    forbidden: NEUTRAL_FORBIDDEN,
    conflict:
      "This changed while you were looking, or the same action is still running. Nothing was changed by this attempt. The page shows the latest.",
    unavailable:
      "The service is not available right now. Try again in a moment: the same action is never applied twice.",
  });
  // A missing record is a record that was removed while the page was open: say so and read again.
  if (api.kind === "not_found") {
    return {
      kind: "not_found",
      message: "This record no longer exists. The page shows the latest.",
      retryable: false,
      reload: true,
    };
  }
  // The diagnostics pages do not tell a 400 from a 422.
  if (kind === "bad_request") {
    return { kind: "invalid", message: STAFF_WORDS.invalid, retryable, reload };
  }
  return { kind, message, retryable, reload };
}
